import React, { useEffect, useState } from "react";
import "./Footer.css";
import Logo from "../../components/Logo";
import { footer as footerLinks, socialHandles } from "../../data.js";
import { FaGlobe } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

// Accreditation images
import accred1 from "../../assets/Accreditations/image (1).png";
import accred2 from "../../assets/Accreditations/image (2).png";
import accred3 from "../../assets/Accreditations/image (3).png";
import bspLogo from "../../assets/Accreditations/logo-bsp-en-noir_2.png";
import oqlfLogo from "../../assets/Accreditations/OQLF_couleur.png";

// Province images
import ontarioProvince from "../../assets/Ontario-Province.png";
import quebecProvince from "../../assets/QuebecProvince.png";

/* Track (max-width: 900px) for mobile-only UI */
function useIsMobile(breakpoint = 900) {
  const query = `(max-width: ${breakpoint}px)`;
  const get = () =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false;
  const [isMobile, setIsMobile] = useState(get);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(query);
    const handler = (e) => setIsMobile(e.matches);
    if (mql.addEventListener) mql.addEventListener("change", handler);
    else mql.addListener(handler);
    setIsMobile(mql.matches);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", handler);
      else mql.removeListener(handler);
    };
  }, [query]);

  return isMobile;
}

export default function Footer() {
  const year = new Date().getFullYear();
  const isMobile = useIsMobile(900);
  const { t, i18n } = useTranslation();

  // translate with fallback to provided string
  const txt = (key, fallback) => t(key, { defaultValue: fallback });

  // Region / language badge (e.g., "CA · EN" or "CA · FR")
  const lg = (i18n.language || "en").slice(0, 2).toUpperCase();
  const regionBadge = `${t("footer.region", { defaultValue: "CA" })} · ${lg}`;

  const isEnglish = i18n.language?.startsWith('en');

  return (
    <>
      <footer id="footer">
        <div className="gw">
          {/* Top bar */}
          <div className="gw__bar">
            <div className="gw__brand">
              <Logo />
              <span className="gw__brandSec" />
            </div>

            <div className="gw__actions">
              <button
                className="gw__locale"
                aria-label={t("footer.aria.changeLocale", { defaultValue: "Change region/language" })}
                type="button"
              >
                <FaGlobe />
                <span>{regionBadge}</span>
              </button>

              {/* Social: driven by data.js -> socialHandles */}
              <nav className="gw__social" aria-label={t("footer.aria.social", { defaultValue: "Social media" })}>
                {socialHandles.map((s, i) => (
                  <a
                    key={`${s.name}-${i}`}
                    href={s.link || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.name}
                    title={s.name}
                  >
                    {s.icon}
                  </a>
                ))}
              </nav>
            </div>
          </div>

          {/* Links: desktop = static columns, mobile = accordion */}
          <div className="gw__grid">
            {footerLinks.map((group, i) =>
              isMobile ? (
                <details className="gw__group" key={i}>
                  <summary className="gw__toggle" aria-controls={`gw-list-${i}`}>
                    {txt(`footer.groups.${i}.title`, group.title)}
                  </summary>
                  <ul className="gw__list" id={`gw-list-${i}`}>
                    {group.routes.map((r, j) => (
                      <li key={j}>
                        <a className="gw__link" href={r.href}>
                          {txt(`footer.groups.${i}.routes.${j}.name`, r.name)}
                        </a>
                      </li>
                    ))}
                  </ul>
                </details>
              ) : (
                <div className="gw__col" key={i}>
                  <h4 className="gw__title">{txt(`footer.groups.${i}.title`, group.title)}</h4>
                  <ul className="gw__list">
                    {group.routes.map((r, j) => (
                      <li key={j}>
                        <a className="gw__link" href={r.href}>
                          {txt(`footer.groups.${i}.routes.${j}.name`, r.name)}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )
            )}

            {/* Locations Section - Added as a column in grid */}
            {!isMobile && (
              <div className="gw__col gw__col--locations">
                <h4 className="gw__title">{t("footer.locations.title", { defaultValue: "Locations" })}</h4>
                <div className="gw__locations-list">
                  <div className="gw__location-item">
                    <img
                      src={quebecProvince}
                      alt={t("footer.locations.quebec.alt", { defaultValue: "Quebec" })}
                      className="gw__location-logo"
                    />
                    <div className="gw__location-info">
                      <span className="gw__location-name">
                        {t("footer.locations.quebec.name", { defaultValue: "Quebec" })}
                      </span>
                      <p className="gw__location-address">
                        {t("footer.locations.quebec.address", {
                          defaultValue: "Suite 620-3285 Boul Cavendish, Montreal, QC H4B 2L9",
                        })}
                      </p>
                      <a href="mailto:info@securityvision.ca" className="gw__location-email">
                        {t("footer.locations.quebec.email", {
                          defaultValue: "info@securityvision.ca",
                        })}
                      </a>
                    </div>
                  </div>
                  <div className="gw__location-item">
                    <img
                      src={ontarioProvince}
                      alt={t("footer.locations.ontario.alt", { defaultValue: "Ontario" })}
                      className="gw__location-logo"
                    />
                    <div className="gw__location-info">
                      <span className="gw__location-name">
                        {t("footer.locations.ontario.name", { defaultValue: "Ontario" })}
                      </span>
                      <p className="gw__location-address">
                        {t("footer.locations.ontario.address", {
                          defaultValue: "Suite 200-150 King Street West, Toronto, ON M5H 1J9",
                        })}
                      </p>
                      <a href="mailto:info@securityvision.ca" className="gw__location-email">
                        {t("footer.locations.ontario.email", {
                          defaultValue: "info@securityvision.ca",
                        })}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Locations for Mobile - Accordion style */}
          {isMobile && (
            <details className="gw__group">
              <summary className="gw__toggle">{t("footer.locations.title", { defaultValue: "Locations" })}</summary>
              <div className="gw__locations-list gw__locations-mobile">
                <div className="gw__location-item">
                  <img
                    src={quebecProvince}
                    alt={t("footer.locations.quebec.alt", { defaultValue: "Quebec" })}
                    className="gw__location-logo"
                  />
                  <div className="gw__location-info">
                    <span className="gw__location-name">
                      {t("footer.locations.quebec.name", { defaultValue: "Quebec" })}
                    </span>
                    <p className="gw__location-address">
                      {t("footer.locations.quebec.address", {
                        defaultValue: "Suite 620-3285 Boul Cavendish, Montreal, QC H4B 2L9",
                      })}
                    </p>
                    <a href="mailto:info@securityvision.ca" className="gw__location-email">
                      {t("footer.locations.quebec.email", {
                        defaultValue: "info@securityvision.ca",
                      })}
                    </a>
                  </div>
                </div>
                <div className="gw__location-item">
                  <img
                    src={ontarioProvince}
                    alt={t("footer.locations.ontario.alt", { defaultValue: "Ontario" })}
                    className="gw__location-logo"
                  />
                  <div className="gw__location-info">
                    <span className="gw__location-name">
                      {t("footer.locations.ontario.name", { defaultValue: "Ontario" })}
                    </span>
                    <p className="gw__location-address">
                      {t("footer.locations.ontario.address", {
                        defaultValue: "Suite 200-150 King Street West, Toronto, ON M5H 1J9",
                      })}
                    </p>
                    <a href="mailto:info@securityvision.ca" className="gw__location-email">
                      {t("footer.locations.ontario.email", {
                        defaultValue: "info@securityvision.ca",
                      })}
                    </a>
                  </div>
                </div>
              </div>
            </details>
          )}

          {/* Accreditations Section */}
          <div className="gw__accreditations">
            <h4 className="gw__accred-title">
              {t("footer.accreditations.title", { defaultValue: "Accreditations & Certifications" })}
            </h4>
            <div className="gw__accred-grid">
              <img src={accred1} alt={t("footer.accreditations.alt1", { defaultValue: "Accreditation 1" })} className="gw__accred-logo" />
              <img src={accred2} alt={t("footer.accreditations.alt2", { defaultValue: "Accreditation 2" })} className="gw__accred-logo" />
              <img src={accred3} alt={t("footer.accreditations.alt3", { defaultValue: "Accreditation 3" })} className="gw__accred-logo" />
              <img src={bspLogo}  alt={t("footer.accreditations.bsp",  { defaultValue: "BSP Certification" })} className="gw__accred-logo" />
              <img src={oqlfLogo} alt={t("footer.accreditations.oqlf", { defaultValue: "OQLF Certification" })} className="gw__accred-logo" />
            </div>
          </div>

          {/* Bottom bar */}
          <div className="gw__bottom">
            <p>
              {t("footer.copyright", {
                defaultValue: "© {{year}} Vision Security Inc. — All rights reserved.",
                year,
              })}
              {" · "}
              <Link to={isEnglish ? "/privacy" : "/fr/privacy"} className="gw__privacy-link">
                {t("footer.privacyPolicy", { defaultValue: "Privacy Policy" })}
              </Link>
              {" · "}
              <Link to={isEnglish ? "/terms" : "/fr/terms"} className="gw__privacy-link">
                {t("footer.termsOfService", { defaultValue: "Terms of Service" })}
              </Link>
            </p>
          </div>
        </div>
      </footer>
    </>
  );
} 
